import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { useEffect } from "react";
import { areAllInsightsFailed, areAllInsightsInProgress, isInsightsNotGenerated, selectAIInsightsStatus, selectAllAIInsights } from "../logic/aiInsightsSelectors";
import AIInsightCard from "./AIInsightCard";
import { fetchInsightsThunk, generateInsightsThunk } from "../logic/aiInsightsThunk";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import styles from './Insights.module.css';
import { Sparkles } from "lucide-react";
import { JobStatusWithSkeleton } from "../../../components/ui/loading/JobStatuswithSkeletonGrid";
import { getCurrentCaseProduct } from "../../caseinfo/logic/Slice";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";


const InsightsContentSection: React.FC = () => {
    const { id: caseId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if(caseId) {
            dispatch(fetchInsightsThunk({ caseId }));
        }
    }, [caseId, dispatch]);

    const loadingStatus = useAppSelector(selectAIInsightsStatus);

    const insightsNotGenerated = useAppSelector(isInsightsNotGenerated);

    const allInsightsInProgress = useAppSelector(areAllInsightsInProgress);

    const allInsightsFailed = useAppSelector(areAllInsightsFailed);

    const aiInsights = useAppSelector(selectAllAIInsights);

    const product = useAppSelector(getCurrentCaseProduct);

    const reloadInsights = () => {
        if(caseId) {
            dispatch(fetchInsightsThunk({ caseId }));
        }
    }

    const generateInsights = () => {
        if(caseId && product){
            dispatch(generateInsightsThunk({caseId, product}));
        }
    }

    if(loadingStatus === 'loading') {
        return (
            <CustomLoadingSpinner
                title="Loading AI Insights..."
                subtitle={RETRIEVING_DATA_SUBTITLE}
            />
        )
    }

    if(loadingStatus === 'failed') {
        return (
            <ErrorDisplay
                title="Sorry, Something went wrong on our end. Please retry."
                onRetry={reloadInsights}
            />
        )
    }

    if(insightsNotGenerated){
        return (
            <div className={styles.generateContainer}>
                <div className={styles.generateCard}>
                    <div className={styles.generateIcon}>
                        <Sparkles size={24} />
                    </div>
                    <div className={styles.generateTextContent}>
                        <h3 className={styles.generateTitle}>AI Insights</h3>
                        <p className={styles.generateDescription}>
                            The Canso agent will analyse data from diverse sources and provide rich insights related to the case to assist in root cause analysis and narrative generation.
                        </p>
                    </div>
                    <button 
                        className={styles.generateButton}
                        onClick={generateInsights}
                    >
                        Generate with AI
                    </button>
                </div>
            </div>
        )
    }

    if(allInsightsInProgress){
        return(
            <div className={styles.loadingContainer}>
                <JobStatusWithSkeleton
                    title='Generating Insights...'
                    description=' The Canso agent will analyse data from diverse sources and provide rich insights related to the case to assist in root cause analysis and narrative generation.'
                />
                
                {/* Refresh button section */}
                <div className={styles.refreshSection}>
                    <div className={styles.refreshButtonContainer}>
                    <button 
                        className={styles.refreshButton}
                        onClick={reloadInsights}
                    >
                        Refresh Status
                    </button>
                    <div className={styles.autoRefreshContainer}>
                        <div className={styles.autoRefreshIcon}>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        )
    }

    if(allInsightsFailed) {
        return (
            <ErrorDisplay
                title="Sorry, Failed to generate Insights. Please retry."
                onRetry={generateInsights}
            />
        )
    }


    return (
        <div>
            {aiInsights.map((insight)=> (
                <AIInsightCard insight={insight} key={insight.insight_key}/>
            ))}
        </div>
    )
}

export default InsightsContentSection;